import type { Placement } from "react-joyride";
import { TOUR, TourTarget } from "./tourTargets";

/**
 * The guided tour as data: re-cutting the tour means editing this
 * array. Steps follow the product thesis — reading → analysis →
 * evaluation → writing — and stay passive ("try it" phrasing), never
 * gating progress on the user performing the action.
 *
 * Bump TOUR_VERSION on a major UI change to re-run the tour for users
 * who completed an older cut (per-user state keys on it from C4).
 */
export const TOUR_VERSION = 1;

/** Key of the tour's entry in the per-user onboarding map. */
export const TOUR_FLOW_KEY = "tour";

/** The first-run offer. The tour is opt-in: it never starts on its own,
 * because arriving in an unfamiliar app and having it drive itself is
 * jarring — especially when sample content is still being prepared. */
export const WELCOME_KEY = "welcome";
export const WELCOME_VERSION = 1;

/** Empty-workspace prompt (onboarding stage 1): its own entry, so
 * dismissing it never touches tour progress. */
export const ADD_READING_PROMPT_KEY = "prompt:add-reading";
export const ADD_READING_PROMPT_VERSION = 1;

/** Records that this account's sample content was seeded, so it
 * happens exactly once — and never again if the user deletes it. */
export const SAMPLE_SEED_KEY = "sample:seeded";
export const SAMPLE_SEED_VERSION = 1;

/** Actions a step may need to make its target visible. Joyride will
 * happily spotlight a hidden element, so preconditions are ours: each
 * step's `prepare` runs (and is awaited) before the step shows. */
export interface TourStepHelpers {
  openSidebar: () => void;
  setBaseView: (view: "reading" | "canvas") => void;
}

export interface TourStepDef {
  target: TourTarget;
  title: string;
  body: string;
  placement?: Placement | "auto" | "center";
  prepare?: (helpers: TourStepHelpers) => void;
  /** Extra ms to wait after prepare, e.g. for a drawer transition. */
  settleMs?: number;
  /**
   * Drop the overlay for this step so the whole app stays usable —
   * the user can actually perform the interaction being taught
   * instead of only reading about it.
   */
  interactive?: boolean;
  /**
   * Advance as soon as a rectangle lands on the canvas. Paired with
   * `interactive`, this turns the step into "your turn" without any
   * per-step completion machinery: the canvas already tracks its
   * rectangles in Redux.
   */
  awaitCanvasDrop?: boolean;
}

export const tourSteps: TourStepDef[] = [
  {
    target: TOUR.sidebar,
    title: "Read and highlight",
    body:
      "Select a passage in the reading to create a highlight and give " +
      "it a category. Every highlight collects here in the sidebar as " +
      "a card — try one when the tour is over.",
    placement: "right",
    prepare: ({ setBaseView, openSidebar }) => {
      setBaseView("reading");
      openSidebar();
    },
    settleMs: 300,
  },
  {
    target: TOUR.baseViewToggle,
    title: "Two views of the same work",
    body:
      "The canvas is where highlights become an argument diagram. " +
      "Switch between reading and canvas here, or with Ctrl+1 and " +
      "Ctrl+2.",
    placement: "bottom",
  },
  {
    target: TOUR.annotationCard,
    title: "Your turn — drag a highlight across",
    body:
      "Drag this card onto the canvas. It becomes a node you can move, " +
      "connect with arrows, and group — still linked to the passage it " +
      "came from. Go ahead; the tour continues once you drop it.",
    placement: "right",
    interactive: true,
    awaitCanvasDrop: true,
    prepare: ({ setBaseView, openSidebar }) => {
      setBaseView("canvas");
      openSidebar();
    },
    settleMs: 300,
  },
  {
    target: TOUR.evaluationToggle,
    title: "Check the argument",
    body:
      "Evaluation mode checks your diagram against the conditions of " +
      "a successful argument and flags what needs attention — circular " +
      "reasoning, unsupported steps, a missing setting.",
    placement: "bottom",
    prepare: ({ setBaseView }) => {
      setBaseView("canvas");
    },
  },
  {
    target: TOUR.paperEditorButton,
    title: "Write it up",
    body:
      "When the argument holds together, open the paper editor and " +
      "draft alongside your diagram — you can import the canvas to " +
      "start from its structure.",
    placement: "bottom",
  },
];
